import type { ReactNode } from 'react'
import { useState, useEffect } from 'react'
import { useUIStore } from '@/shared/stores/uiStore'
import { useTimerStore } from '@/shared/stores/timerStore'
import Sidebar from './Sidebar'
import { Header } from './Header'
import { MobileHeader } from './MobileHeader'
import { TimerWidget } from '@/features/timer/components/TimerWidget'
import { useTimerSync } from '@/features/timer/hooks/useTimerSync'
import { useTimerAutoSave } from '@/features/timer/hooks/useTimerAutoSave'
import { useIsMobile } from '@/shared/hooks/useMediaQuery'
import { projectService } from '@/shared/services/projectService'
import type { Project } from '@/shared/types/entities'

interface AppLayoutProps {
  children: ReactNode
}

export function AppLayout({ children }: Readonly<AppLayoutProps>) {
  const { sidebarOpen, sidebarCollapsed } = useUIStore()
  const { isMinimized } = useTimerStore()
  const isMobile = useIsMobile()
  const [projects, setProjects] = useState<Project[]>([])
  const showTimerAsPopup = isMinimized
  
  // Initialize timer sync and auto-save
  useTimerSync()
  useTimerAutoSave()
  
  // Fetch projects for timer
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await projectService.getProjects()
        setProjects(Array.isArray(data) ? data : [])
      } catch {
        setProjects([])
      }
    }
    loadProjects()
  }, [])
  
  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar - hidden on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-border/50 bg-card transition-all duration-300 lg:static hidden lg:flex
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        <Sidebar collapsed={sidebarCollapsed} />
      </aside>
      
      {/* Overlay for mobile sidebar */}
      {sidebarOpen && !isMobile && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden cursor-pointer border-0 p-0"
          onClick={() => useUIStore.getState().toggleSidebar()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              useUIStore.getState().toggleSidebar()
            }
          }}
          aria-label="Close sidebar"
        />
      )}
      
      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header on small screens */}
        <div className="lg:hidden">
          <MobileHeader />
        </div>
        {/* Desktop header on large screens */}
        <div className="hidden lg:block">
          <Header />
        </div>
        
        <main className="flex flex-1 overflow-auto p-4 lg:p-6 lg:pt-6">
          <div className={`flex-1 w-full ${!showTimerAsPopup ? 'lg:mr-6' : ''}`}>
            {children}
          </div>
          {/* Timer sidebar - hidden when minimized to popup */}
          {!showTimerAsPopup && (
            <div className="hidden w-[420px] lg:block flex-shrink-0">
              <TimerWidget projects={projects} />
            </div>
          )}
        </main>
        {/* Timer popup overlay when minimized */}
        {showTimerAsPopup && (
          <TimerWidget projects={projects} />
        )}
      </div>
    </div>
  )
}

export default AppLayout
